import React, { useMemo, useState } from "react";
import { type Table } from "@tanstack/react-table";

import { useListCfrPurchaseReleaseBreakdown, PurchaseReleaseBreakdownItem } from "@/lib/api/cfr";
import { CommodityOptions } from "@/lib/api/commodity";
import { createDateLabel } from "@/lib/utils";
import { DataTable } from "@/components/ui/DataTable/index";
import { type DataTableColumn } from "@/components/ui/DataTable/index";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { IndeterminateCheckbox } from "./IndeterminateCheckbox";
import { BulkPurchaseReleaseBreakdownSendDialog } from "./BulkPurchaseReleaseBreakdownSendDialog";
import { downloadBinaryData } from "@/lib/browser/download";
import * as ExcelComposer from "@/lib/excel/composer";
import type { CellValue } from "@/lib/excel/composer";
import { numberToExcelColumn } from "@/lib/excel/common";
import { MimeType } from "@/lib/api/utils";

export function PurchaseReleaseBreakdown() {
  const breakdownResult = useListCfrPurchaseReleaseBreakdown();
  const [selectedTab, setSelectedTab] = useState<"notSent" | "sent" | "signed" | "other">("notSent");
  const [isBulkSendDialogOpen, setIsBulkSendDialogOpen] = useState(false);
  const [selectedNotSentRows, setSelectedNotSentRows] = useState<PurchaseReleaseBreakdownItem[]>([]);
  const isLoading = breakdownResult.isLoading;
  const isError = breakdownResult.error || (!!breakdownResult.data && !breakdownResult.data?.success);

  const breakdownData = breakdownResult.data?.success
    ? breakdownResult.data.data
    : null;

  const { columns, rows } = useMemo((): {
    columns: DataTableColumn<PurchaseReleaseBreakdownItem>[];
    rows: PurchaseReleaseBreakdownItem[];
  } => {
    const baseColumns: DataTableColumn<PurchaseReleaseBreakdownItem>[] = [
      {
        id: "commodity_type",
        accessorKey: "commodity_type",
        header: "Commodity",
        accessorFn: (row) => {
          const commodity_value = row.commodity_type || "";
          return CommodityOptions.find((option) => option.value === commodity_value)?.label || commodity_value;
        },
      },
      {
        id: "crop_year",
        accessorKey: "crop_year",
        header: "Crop Year",
      },
      {
        id: "customer",
        accessorKey: "customer",
        header: "Customer",
        accessorFn: (row) => row.customer?.long_name || "N/A",
      },
      {
        id: "destination",
        accessorKey: "destination",
        header: "Destination",
        accessorFn: (row) => row.destination?.long_name || "N/A",
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        accessorFn: (row) => createDateLabel(row.created_at),
      },
    ];

    if (selectedTab === "notSent") {
      baseColumns.unshift({
        id: "selection-box",
        enableFiltering: false,
        enableSorting: false,
        disableRowClick: true,
        header: ({ table }) => (
          <IndeterminateCheckbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <IndeterminateCheckbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 50,
      });
    }

    // Add status-specific columns
    if (selectedTab === "sent" || selectedTab === "signed" || selectedTab === "other") {
      baseColumns.push({
        id: "last_status",
        accessorKey: "last_status",
        header: "Status",
        accessorFn: (row) => row.last_status || "",
      });
    }

    if (selectedTab === "signed") {
      baseColumns.push({
        id: "completed_at",
        accessorKey: "completed_at",
        header: "Completed",
        accessorFn: (row) => row.completed_at ? createDateLabel(row.completed_at) : "",
      });
    }

    if (selectedTab === "other") {
      baseColumns.push(
        {
          id: "cancelled_at",
          accessorKey: "cancelled_at",
          header: "Cancelled",
          accessorFn: (row) => row.cancelled_at ? createDateLabel(row.cancelled_at) : "",
        },
        {
          id: "revoked_at",
          accessorKey: "revoked_at",
          header: "Revoked",
          accessorFn: (row) => row.revoked_at ? createDateLabel(row.revoked_at) : "",
        },
        {
          id: "declined_at",
          accessorKey: "declined_at",
          header: "Declined",
          accessorFn: (row) => row.declined_at ? createDateLabel(row.declined_at) : "",
        },
        {
          id: "expired_at",
          accessorKey: "expired_at",
          header: "Expired",
          accessorFn: (row) => row.expired_at ? createDateLabel(row.expired_at) : "",
        }
      );
    }

    let rows: PurchaseReleaseBreakdownItem[] = [];
    if (breakdownData) {
      switch (selectedTab) {
        case "notSent":
          rows = breakdownData.notSent || [];
          break;
        case "sent":
          rows = breakdownData.sent || [];
          break;
        case "signed":
          rows = breakdownData.signed || [];
          break;
        case "other":
          rows = breakdownData.other || [];
          break;
      }
    }

    return { columns: baseColumns, rows };
  }, [selectedTab, breakdownData]);

  const handleBulkDialogOpenChange = (open: boolean) => {
    setIsBulkSendDialogOpen(open);
    if (!open) {
      setSelectedNotSentRows([]);
    }
  };

  const handleExcelExport = async () => {
    if (!breakdownData) return;

    // Status priority: Signed > Sent > Not Sent > Other
    const statusPriority: Record<string, number> = {
      "Signed": 4,
      "Sent": 3,
      "Not Sent": 2,
      "Other": 1,
    };

    // Color fills for each status
    type FillType = {
      type: "pattern";
      pattern: "solid";
      fgColor: { argb: string };
    };
    const statusColors: Record<string, FillType> = {
      "Signed": {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF90EE90" }, // Light green
      },
      "Sent": {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFFF00" }, // Yellow
      },
      "Not Sent": {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF6B6B" }, // Light red
      },
      "Other": {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF87CEEB" }, // Sky blue
      },
    };

    // Helper function to get highest priority status
    const getHighestPriorityStatus = (statuses: string[]): string | null => {
      if (statuses.length === 0) return null;
      return statuses.reduce((highest, current) => {
        const currentPriority = statusPriority[current] || 0;
        const highestPriority = statusPriority[highest] || 0;
        return currentPriority > highestPriority ? current : highest;
      });
    };

    // Combine all tabs with their status labels
    const allItems: Array<PurchaseReleaseBreakdownItem & { status: string }> = [
      ...(breakdownData.notSent || []).map((item: PurchaseReleaseBreakdownItem) => ({ ...item, status: "Not Sent" })),
      ...(breakdownData.sent || []).map((item: PurchaseReleaseBreakdownItem) => ({ ...item, status: "Sent" })),
      ...(breakdownData.signed || []).map((item: PurchaseReleaseBreakdownItem) => ({ ...item, status: "Signed" })),
      ...(breakdownData.other || []).map((item: PurchaseReleaseBreakdownItem) => ({ ...item, status: item.last_status || "Other" })),
    ];

    // Group items by commodity first
    const itemsByCommodity = new Map<string, Array<PurchaseReleaseBreakdownItem & { status: string }>>();
    allItems.forEach(item => {
      const commodity = item.commodity_type || "";
      if (!commodity) return;
      
      if (!itemsByCommodity.has(commodity)) {
        itemsByCommodity.set(commodity, []);
      }
      itemsByCommodity.get(commodity)!.push(item);
    });

    // Create Excel workbook
    const composer = new ExcelComposer.ExcelComposer();

    // Process each commodity separately
    const sortedCommodities = Array.from(itemsByCommodity.keys()).sort((a, b) => {
      const labelA = CommodityOptions.find(opt => opt.value === a)?.label || a;
      const labelB = CommodityOptions.find(opt => opt.value === b)?.label || b;
      return labelA.localeCompare(labelB);
    });

    for (const commodity of sortedCommodities) {
      const commodityItems = itemsByCommodity.get(commodity)!;
      
      // Get unique destinations for this commodity
      const destinationMap = new Map<string, string>();
      commodityItems.forEach(item => {
        if (item.destination?.id && item.destination?.long_name) {
          destinationMap.set(item.destination.id, item.destination.long_name);
        }
      });
      const destinations = Array.from(destinationMap.keys()).sort((a, b) => {
        const nameA = destinationMap.get(a) || a;
        const nameB = destinationMap.get(b) || b;
        return nameA.localeCompare(nameB);
      });

      // Group by customer and crop_year for this commodity
      type GroupKey = string;
      type DestinationStatuses = Map<string, string[]>; // destination_id -> array of statuses
      
      const groupedData = new Map<GroupKey, {
        cropYear: number;
        customer: string;
        destinationStatuses: DestinationStatuses;
      }>();

      commodityItems.forEach(item => {
        const cropYear = item.crop_year;
        const customerId = item.customer_id || "";
        const customerName = item.customer?.long_name || "N/A";
        const destinationId = item.destination?.id || "";

        if (!customerId || !destinationId) return;

        const groupKey = `${customerId}|${cropYear}`;
        
        if (!groupedData.has(groupKey)) {
          groupedData.set(groupKey, {
            cropYear,
            customer: customerName,
            destinationStatuses: new Map(),
          });
        }

        const group = groupedData.get(groupKey)!;
        if (!group.destinationStatuses.has(destinationId)) {
          group.destinationStatuses.set(destinationId, []);
        }
        group.destinationStatuses.get(destinationId)!.push(item.status);
      });

      // Sort by crop year (descending), then customer
      const sortedGroups = Array.from(groupedData.entries()).sort((a, b) => {
        const [keyA, dataA] = a;
        const [keyB, dataB] = b;
        if (dataA.cropYear !== dataB.cropYear) {
          return dataB.cropYear - dataA.cropYear; // Descending
        }
        return dataA.customer.localeCompare(dataB.customer);
      });

      if (sortedGroups.length === 0) continue;

      // Get commodity label for sheet name
      const commodityLabel = CommodityOptions.find(
        option => option.value === commodity
      )?.label || commodity;

      // Create sheet for this commodity
      composer.createSheet(commodityLabel);

      // Build headers (no Commodity column since each sheet is for one commodity)
      const headers: string[] = ["Crop Year", "Customer", ...destinations.map(destId => destinationMap.get(destId) || destId)];

      // Build data rows
      const dataRows: CellValue[][] = [];

      sortedGroups.forEach(([_, group]) => {
        const row: CellValue[] = [
          { value: group.cropYear },
          { value: group.customer },
        ];

        // Add destination columns with statuses and colors
        destinations.forEach(destId => {
          const statuses = group.destinationStatuses.get(destId) || [];
          
          if (statuses.length === 0) {
            // Empty cell - no fill, no value
            row.push({ value: "" });
          } else {
            const statusText = statuses.join(", ");
            const highestPriorityStatus = getHighestPriorityStatus(statuses);
            
            // Only apply fill if we have a status
            const cellValue: CellValue = {
              value: statusText,
            };
            
            if (highestPriorityStatus) {
              cellValue.fill = statusColors[highestPriorityStatus];
            }
            
            row.push(cellValue);
          }
        });

        dataRows.push(row);
      });

      // Build column widths configuration
      const columnWidths: Record<string, { width: number }> = {
        A: { width: 12 }, // Crop Year
        B: { width: 30 }, // Customer
      };
      
      // Add destination columns (starting at column C, index 3)
      destinations.forEach((_, index) => {
        const columnLetter = numberToExcelColumn(3 + index); // C=3, D=4, E=5, etc.
        columnWidths[columnLetter] = { width: 22 };
      });

      // Add section to workbook
      composer.addSection({
        headers,
        data: dataRows,
        styling: {
          header: {
            bold: true,
          },
          columns: columnWidths,
        },
      } as ExcelComposer.SectionConfig, { startRow: 1, startColumn: 1 });
    }

    // Generate and download Excel file
    const buffer = await composer.getBuffer();
    downloadBinaryData(buffer, "purchase_release_breakdown.xlsx", MimeType.Xlsx);
  };

  const excelExportButton = ({ table }: { table: Table<PurchaseReleaseBreakdownItem> }) => {
    return (
      <Button
        variant="outline"
        onClick={handleExcelExport}
        disabled={!breakdownData}
      >
        CFR Status Excel
      </Button>
    );
  };

  const notSentBulkSendButton = ({ table }: { table: Table<PurchaseReleaseBreakdownItem> }) => {
    const selectedRows = table.getFilteredSelectedRowModel().rows;
    return (
      <Button
        variant="outline"
        onClick={() => {
          setSelectedNotSentRows(selectedRows.map((row) => row.original));
          setIsBulkSendDialogOpen(true);
        }}
        disabled={selectedRows.length === 0}
      >
        Bulk Send ({selectedRows.length})
      </Button>
    );
  };

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <p className="text-destructive">
          Failed to load purchase release breakdown. Please refresh and try again.
        </p>
      </div>
    );
  }

  const notSentCount = breakdownData?.notSent?.length || 0;
  const sentCount = breakdownData?.sent?.length || 0;
  const signedCount = breakdownData?.signed?.length || 0;
  const otherCount = breakdownData?.other?.length || 0;

  return (
    <Tabs value={selectedTab} onValueChange={(value) => setSelectedTab(value as any)} className="h-full flex flex-col">
      <TabsList className="flex flex-row gap-2 mb-2">
        <TabsTrigger value="notSent">Not Sent ({notSentCount})</TabsTrigger>
        <TabsTrigger value="sent">Sent ({sentCount})</TabsTrigger>
        <TabsTrigger value="signed">Signed ({signedCount})</TabsTrigger>
        <TabsTrigger value="other">Other ({otherCount})</TabsTrigger>
      </TabsList>

      <TabsContent value="notSent" className="mt-0">
        <DataTable
          stickyHeaders
          tableClassName="h-[calc(50vh-200px)] overflow-y-scroll"
          data={rows}
          columns={columns}
          enableSorting
          enableMultiSort
          enableFiltering
          enableGrouping
          enableColumnVisibility
          enableMultiSelect={selectedTab === "notSent"}
          customToolbarButtons={selectedTab === "notSent" ? [excelExportButton, notSentBulkSendButton] : [excelExportButton]}
          filterStyle="select"
          filterMode="internal"
          emptyMessage={isLoading ? "Loading ..." : "No items found"}
          defaultSorting={[{ id: "created_at", desc: true }]}
        />
      </TabsContent>

      <TabsContent value="sent" className="mt-0">
        <DataTable
          stickyHeaders
          tableClassName="h-[calc(50vh-200px)] overflow-y-scroll"
          data={rows}
          columns={columns}
          enableSorting
          enableMultiSort
          enableFiltering
          enableGrouping
          enableColumnVisibility
          customToolbarButtons={[excelExportButton]}
          filterStyle="select"
          filterMode="internal"
          emptyMessage={isLoading ? "Loading ..." : "No items found"}
          defaultSorting={[{ id: "created_at", desc: true }]}
        />
      </TabsContent>

      <TabsContent value="signed" className="mt-0">
        <DataTable
          stickyHeaders
          tableClassName="h-[calc(50vh-200px)] overflow-y-scroll"
          data={rows}
          columns={columns}
          enableSorting
          enableMultiSort
          enableFiltering
          enableGrouping
          enableColumnVisibility
          customToolbarButtons={[excelExportButton]}
          filterStyle="select"
          filterMode="internal"
          emptyMessage={isLoading ? "Loading ..." : "No items found"}
          defaultSorting={[{ id: "completed_at", desc: true }]}
        />
      </TabsContent>

      <TabsContent value="other" className="mt-0">
        <DataTable
          stickyHeaders
          tableClassName="h-[calc(50vh-200px)] overflow-y-scroll"
          data={rows}
          columns={columns}
          enableSorting
          enableMultiSort
          enableFiltering
          enableGrouping
          enableColumnVisibility
          customToolbarButtons={[excelExportButton]}
          filterStyle="select"
          filterMode="internal"
          emptyMessage={isLoading ? "Loading ..." : "No items found"}
          defaultSorting={[{ id: "created_at", desc: true }]}
        />
      </TabsContent>

      {isBulkSendDialogOpen && (
        <BulkPurchaseReleaseBreakdownSendDialog
          open={isBulkSendDialogOpen}
          onOpenChange={handleBulkDialogOpenChange}
          selectedItems={selectedNotSentRows}
        />
      )}
    </Tabs>
  );
}

